import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, Polyline } from '@react-google-maps/api';
import { getCurrentSession } from '../services/authService';
import useFocusRefetch from '../hooks/useFocusRefetch';
import { cx } from '../ui/cx';
import { Card, StatusBadge, Select, Button, DropdownMenu, Alert, useToast, TextInput, Field, Pagination } from '../ui/primitives';
import { IconRefresh, IconUser, IconClock, IconPin, IconCircleCheck, IconWarning, IconX, IconSearch, IconEdit, IconTrash, IconDots } from '../ui/icons';
import { Modal } from '../ui/Modal';
import TripEditModal from './TripEditModal';

const API_BASE = process.env.REACT_APP_MOVORRA_API_URL || 'http://localhost:8081';
const DEFAULT_CENTER = { lat: 41.5067, lng: -90.5151 };
const mapStyle = { width: '100%', height: '100%' };
const ARROW_ICON = { path: 'M 0,0 L -3,6 L 3,6 Z', strokeWeight: 0, scale: 1.2, rotation: 0 };
const ROUTE_COLORS = ['#0B2540', '#2563EB', '#7C3AED', '#D97706', '#059669', '#DC2626', '#6366F1', '#0891B2'];

const WHEN_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'week', label: 'Week' },
  { value: 'all', label: 'All' },
];

// Controlled trip vocabulary (+ assignment state). Mirrors the design toolbar.
const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'en_route', label: 'En route' },
  { value: 'picked_up', label: 'Picked up' },
  { value: 'dropped_off', label: 'Dropped off' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Dispatcher acts only up to ASSIGNED; EN_ROUTE+ is driver-driven (read-only).
const ACTIONABLE_PRE = ['SCHEDULED', 'CONFIRMED', 'ASSIGNED'];
const LIVE_STATUSES = ['EN_ROUTE', 'PICKED_UP', 'DROPPED_OFF'];

function formatTime(dt) {
  if (!dt) return '-';
  const d = new Date(dt);
  if (isNaN(d.getTime())) return dt;
  return d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function formatLocation(loc) {
  if (!loc) return '-';
  const parts = [loc.addressLine1, loc.city, loc.state].filter(Boolean);
  let result = parts.join(', ') || '-';
  if (loc.airportCode) result += ` (${loc.airportCode})`;
  return result;
}

function shortLoc(loc) {
  if (!loc) return '-';
  if (loc.airportCode) return loc.airportCode;
  return loc.city || loc.addressLine1 || '-';
}

// Several lowercased renderings of a pickup date so free-text search matches common typing:
// "sun, jun 28, 2026", "jun 28", "6/28/2026", "6/28", "2026-06-28".
function dateSearchText(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  if (isNaN(d.getTime())) return '';
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return [
    d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`,
    `${d.getMonth() + 1}/${d.getDate()}`,
    iso,
  ].join(' ').toLowerCase();
}

function formatElapsed(ms) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

// Live "time since the trip started" for the ops board. Self-contained 1s ticker so only
// this badge re-renders each second (not the whole board / map). The driver never sees this —
// it's an ops-only readout of the running trip, computed from Trip.startedAt.
function ElapsedTime({ startedAt }) {
  const [, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  if (!startedAt) return null;
  const ms = Date.now() - new Date(startedAt).getTime();
  if (isNaN(ms)) return null;
  return <span className="font-mono tabular-nums">· {formatElapsed(ms)}</span>;
}

// Ride/vehicle label for a row. The customer's chosen service class (rideName,
// resolved from the booking-trip's rideTypeId via /api/ride-types) is the primary
// label and is present on EVERY row — assigned or not. The concrete vehicleName is
// appended only once a vehicle is assigned; tripType is a last-resort fallback.
// All from existing payloads — no backend change.
// Pull the server's error reason out of a failed response (GlobalExceptionHandler returns
// {"error": "..."}), so a 409 like an overlapping-trip conflict shows the real reason.
async function readErr(res) {
  try { const b = await res.json(); return b.error || b.message || null; } catch (e) { return null; }
}

function vehicleLabel(bt, rideName) {
  if (!bt) return null;
  const cls = rideName || (bt.tripType ? bt.tripType.charAt(0) + bt.tripType.slice(1).toLowerCase() : null);
  const veh = bt.vehicleName || null;
  if (cls && veh && cls !== veh) return `${cls} · ${veh}`;
  return cls || veh || null;
}

function isToday(dt) { if (!dt) return false; return new Date(dt).toDateString() === new Date().toDateString(); }
function isTomorrow(dt) { if (!dt) return false; const t = new Date(); t.setDate(t.getDate()+1); return new Date(dt).toDateString() === t.toDateString(); }
function isThisWeek(dt) { if (!dt) return false; const d = new Date(dt), n = new Date(), e = new Date(); e.setDate(n.getDate()+7); return d >= n && d <= e; }

// Date-group helpers for the Week/All views. Rows are bucketed by pickup *day*;
// the bucket gets a human label (Today / Tomorrow / "Tue, Jun 10") and a sort
// value (midnight ms) so groups run chronologically. Undated rows sort last.
function dayLabel(dt) {
  if (!dt) return 'Unscheduled';
  const d = new Date(dt);
  if (isNaN(d.getTime())) return 'Unscheduled';
  if (isToday(dt)) return 'Today';
  if (isTomorrow(dt)) return 'Tomorrow';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
function daySortVal(dt) {
  if (!dt) return Infinity;
  const d = new Date(dt);
  return isNaN(d.getTime()) ? Infinity : new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

const fmtUSD = (n) => '$' + Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

// Fetch windows (pickup date, relative to now). The trip list loads a year ahead so the
// "All" filter really shows every upcoming booking — a 14-day horizon silently dropped
// trips booked further out. Driver occupancy stays on the short window: it computes ORS
// durations per trip on every 12s poll, and busy/free only matters near-term (the backend
// still rejects a far-future overlapping assignment with a 409).
const LIST_DAYS_BACK = 7;
const LIST_DAYS_AHEAD = 365;
const OCCUPANCY_DAYS_AHEAD = 14;

// ISO local-datetime query params (no trailing Z) for a [now - back, now + ahead] window.
function rangeParams(daysBack, daysAhead) {
  const now = new Date();
  const from = new Date(now); from.setDate(from.getDate() - daysBack);
  const to = new Date(now); to.setDate(to.getDate() + daysAhead);
  return `from=${from.toISOString().replace('Z','')}&to=${to.toISOString().replace('Z','')}`;
}

// Stable identity for a row. A booking can have several booking-trips (several
// rows), so selection must key on both ids — keying on bookingId alone would
// resolve every row of a multi-trip booking to the first one.
const rowKey = (r) => `${r.bookingId}-${r.trip.bookingTripId}`;

// Relative "Synced … ago" — computed client-side from the last successful fetch
// time (the focus-refetch hook doesn't track sync time). No sync infrastructure.
function relTime(date) {
  if (!date) return '—';
  const s = Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 1000));
  if (s < 5) return 'just now';
  if (s < 60) return s + 's ago';
  const m = Math.floor(s / 60);
  if (m < 60) return m + 'm ago';
  return Math.floor(m / 60) + 'h ago';
}

// Local segmented control for the WHEN filter (styled to brand tokens). We do
// NOT restyle the shared Tabs primitive — this is a dense, single-row control.
function Segmented({ options, value, onChange, className = '' }) {
  return (
    <div className={cx('inline-flex items-center gap-0.5 rounded-btn border border-border bg-white p-0.5', className)} role="tablist">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            className={cx(
              'rounded-[6px] px-3 py-1.5 text-13 font-medium cursor-pointer select-none transition-colors duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
              active ? 'bg-primary text-white shadow-card' : 'text-body/70 hover:text-primary hover:bg-primary/5'
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Kpi({ label, value, sub, subTone }) {
  return (
    <Card className="px-4 py-3">
      <div className="text-11 font-semibold uppercase tracking-wider text-body/50">{label}</div>
      <div className="mt-1.5 text-[30px] leading-none font-bold text-primary font-mono tabular-nums tracking-tight">{value}</div>
      {sub && (
        <div className={cx('mt-1.5 text-[12px] font-medium', subTone === 'amber' ? 'text-primary' : 'text-body/55')}>
          {subTone === 'amber' && <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent mr-1.5 align-middle" />}
          {sub}
        </div>
      )}
    </Card>
  );
}

// Action trigger styled like Button (sm). The shared DropdownMenu wraps its
// trigger in a <button>, so the trigger itself must NOT be a <button> — we
// render a button-styled span to avoid nested interactive elements.
const TRIGGER_BASE =
  'inline-flex items-center justify-center gap-2 rounded-btn border font-medium cursor-pointer select-none px-3 py-1.5 text-xs whitespace-nowrap transition-[background-color,border-color,box-shadow,transform] duration-150';
const TRIGGER_PRIMARY = 'border-transparent bg-primary text-white shadow-card hover:bg-primary-dark';
const TRIGGER_OUTLINE = 'border-border bg-white text-body hover:bg-surface';

function OpsDispatchBoard({ authSession }) {
  const toast = useToast();
  const [bookings, setBookings] = useState([]);
  const [trips, setTrips] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [rideTypes, setRideTypes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [occupancy, setOccupancy] = useState([]); // schedule-aware availability per driver
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [timeFilter, setTimeFilter] = useState('today');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editRow, setEditRow] = useState(null); // row being edited in the TripEditModal
  const [page, setPage] = useState(0); // booking-trips list pagination

  // Reset to the first page whenever the filters/search change.
  useEffect(() => { setPage(0); }, [timeFilter, statusFilter, search]);
  const [mapCollapsed, setMapCollapsed] = useState(false);
  const [rightTab, setRightTab] = useState('map'); // 'map' | 'drivers' — content of the right pane
  const [lastSync, setLastSync] = useState(null);
  const [liveOk, setLiveOk] = useState(true); // last fetch/poll succeeded? drives the header indicator
  const [, setTick] = useState(0);
  // "Cancel booking" — the row awaiting a cancel confirmation (PUT /api/bookings/cancel).
  const [cancelRow, setCancelRow] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  // "Delete test trip" — dev-only permanent delete, gated on the backend test-tools flag.
  const [testToolsEnabled, setTestToolsEnabled] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  // Retained for the deferred shared/charter linking feature (hidden in 8ef23e5) —
  // backs the link/unlink handlers below; not surfaced in the dispatcher UI.
  const [linkingTrip, setLinkingTrip] = useState(null);
  const mapRef = useRef(null);

  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '' });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let token = authSession?.idToken;
      try { const s = await getCurrentSession(); token = s.idToken; } catch (e) {}
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const listRange = rangeParams(LIST_DAYS_BACK, LIST_DAYS_AHEAD);
      const occRange = rangeParams(LIST_DAYS_BACK, OCCUPANCY_DAYS_AHEAD);
      const [bkRes, tripRes, assRes, drvRes, locRes, rtRes, vehRes, custRes, occRes] = await Promise.all([
        fetch(`${API_BASE}/api/bookings/with-trips/range?${listRange}`, { headers }),
        fetch(`${API_BASE}/api/trips/range?${listRange}`, { headers }),
        fetch(`${API_BASE}/api/driver-assignments`, { headers }),
        fetch(`${API_BASE}/api/drivers`, { headers }),
        fetch(`${API_BASE}/api/driver-location/all`, { headers }),
        fetch(`${API_BASE}/api/ride-types`, { headers }),
        fetch(`${API_BASE}/api/vehicles`, { headers }),
        fetch(`${API_BASE}/api/customers`, { headers }),
        fetch(`${API_BASE}/api/dispatch/driver-occupancy?${occRange}`, { headers }),
      ]);
      if (bkRes.ok) setBookings(await bkRes.json());
      if (tripRes.ok) setTrips(await tripRes.json());
      if (vehRes.ok) setVehicles(await vehRes.json());
      if (assRes.ok) setAssignments(await assRes.json());
      if (drvRes.ok) setDrivers(await drvRes.json());
      if (locRes.ok) setLocations(await locRes.json());
      if (rtRes.ok) setRideTypes(await rtRes.json());
      if (custRes.ok) setCustomers(await custRes.json());
      if (occRes.ok) setOccupancy(await occRes.json());
      setLastSync(new Date());
      setLiveOk(true);
    } catch (err) { setError('Failed to load dispatch data'); setLiveOk(false); }
    finally { setLoading(false); setHasLoaded(true); }
  }, [authSession]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  const refresh = useFocusRefetch(fetchAll);

  // Lightweight background poll (~12s): refresh ONLY the dynamic data — the two
  // range fetches and live driver locations — leaving the static reference data
  // vehicles and the loading spinner alone. Drivers, customers and ride-types are
  // refetched on-demand: data that just arrived (e.g. a booking via webchat, or a
  // trip another dispatcher just assigned to a freshly-onboarded driver) may
  // reference an entity we haven't loaded, which would otherwise render as a bare
  // booking number / "Driver #id" — so we refetch each list only when a polled
  // record carries an id we don't yet have.
  // Runs on top of the initial load and focus-refetch; failures are swallowed so
  // a transient blip keeps the last good data on screen instead of erroring.
  const pollLive = useCallback(async () => {
    try {
      let token = authSession?.idToken;
      try { const s = await getCurrentSession(); token = s.idToken; } catch (e) {}
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const listRange = rangeParams(LIST_DAYS_BACK, LIST_DAYS_AHEAD);
      const occRange = rangeParams(LIST_DAYS_BACK, OCCUPANCY_DAYS_AHEAD);
      const [bkRes, tripRes, locRes, occRes] = await Promise.all([
        fetch(`${API_BASE}/api/bookings/with-trips/range?${listRange}`, { headers }),
        fetch(`${API_BASE}/api/trips/range?${listRange}`, { headers }),
        fetch(`${API_BASE}/api/driver-location/all`, { headers }),
        fetch(`${API_BASE}/api/dispatch/driver-occupancy?${occRange}`, { headers }),
      ]);
      if (tripRes.ok) {
        const tripData = await tripRes.json();
        setTrips(tripData);
        const knownDrv = new Set(drivers.map(d => d.driverId));
        if (tripData.some(t => t.driverId && !knownDrv.has(t.driverId))) {
          const r = await fetch(`${API_BASE}/api/drivers`, { headers });
          if (r.ok) setDrivers(await r.json());
        }
      }
      if (locRes.ok) setLocations(await locRes.json());
      if (occRes.ok) setOccupancy(await occRes.json());
      if (bkRes.ok) {
        const bkData = await bkRes.json();
        setBookings(bkData);
        const knownCust = new Set(customers.map(c => c.customerId));
        const knownRt = new Set(rideTypes.map(rt => rt.rideTypeId));
        if (bkData.some(b => b.customerId && !knownCust.has(b.customerId))) {
          const r = await fetch(`${API_BASE}/api/customers`, { headers });
          if (r.ok) setCustomers(await r.json());
        }
        if (bkData.some(b => (b.trips || []).some(bt => bt.rideTypeId && !knownRt.has(bt.rideTypeId)))) {
          const r = await fetch(`${API_BASE}/api/ride-types`, { headers });
          if (r.ok) setRideTypes(await r.json());
        }
      }
      setLastSync(new Date());
      setLiveOk(true);
    } catch (err) { setLiveOk(false); /* keep last good data on a transient poll failure, but flag the indicator */ }
  }, [authSession, customers, rideTypes, drivers]);

  useEffect(() => {
    const id = setInterval(pollLive, 12000);
    return () => clearInterval(id);
  }, [pollLive]);

  // Keep the "Synced … ago" label current (the poll already refreshes the data).
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const getHeaders = async () => {
    let token = authSession?.idToken;
    try { const s = await getCurrentSession(); token = s.idToken; } catch (e) {}
    const h = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  // Discover once whether the dev/demo test tools (permanent delete + notification
  // suppression) are live in this environment. The endpoint only exists when the backend
  // flag is on, so a 404/error simply means "off".
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await getHeaders();
        const res = await fetch(`${API_BASE}/api/ops/test-tools/status`, { headers });
        if (!cancelled && res.ok) {
          const data = await res.json();
          setTestToolsEnabled(!!data.enabled);
        }
      } catch (e) { /* off */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ensure a standalone Trip exists for a booking-trip row and return its id, creating a
  // private, driverless Trip (status CONFIRMED) on the fly when the row hasn't been
  // dispatched yet. This is the single materialisation point so a driver/vehicle/status
  // change never creates two Trips. Updates local state so the row resolves the new link.
  const ensureTrip = async (row) => {
    if (row.linkedTrip?.tripId) return row.linkedTrip.tripId;
    const headers = await getHeaders();
    const res = await fetch(`${API_BASE}/api/trips/from-booking-trip/${row.trip.bookingTripId}`, {
      method: 'POST', headers,
    });
    if (!res.ok) throw new Error(`create-trip ${res.status}`);
    const created = await res.json();
    setTrips(prev => [...prev, created]);
    setBookings(prev => prev.map(b => ({
      ...b,
      trips: (b.trips || []).map(x => x.bookingTripId === row.trip.bookingTripId ? { ...x, tripId: created.tripId } : x),
    })));
    return created.tripId;
  };

  // Assign / change a driver on a row. Routing is UNCHANGED from before — only
  // the trigger UI (DropdownMenu) and feedback (Toast) are new. Each branch now
  // surfaces a success/failure Toast instead of failing silently.
  const assignDriver = async (row, driver) => {
    const bt = row.trip;
    const driverName = `${driver.firstName} ${driver.lastName}`;
    const wasAssigned = !!row.driver;
    try {
      const headers = await getHeaders();
      let ok = false;
      let failMsg = null;
      if (row.linkedTrip?.tripId) {
        // Trip exists — reassign driver on the trip.
        const res = await fetch(`${API_BASE}/api/trips/${row.linkedTrip.tripId}/assign-driver/${driver.driverId}`, { method: 'PATCH', headers });
        if (res.ok) {
          const updated = await res.json();
          setTrips(prev => prev.map(t => t.tripId === updated.tripId ? updated : t));
          ok = true;
        } else { failMsg = await readErr(res); }
      } else if (row.assignment) {
        // Legacy: update existing driver-assignment.
        const res = await fetch(`${API_BASE}/api/driver-assignments/${row.assignment.assignmentId}`, {
          method: 'PUT', headers,
          body: JSON.stringify({
            bookingTripId: bt.bookingTripId,
            driverId: driver.driverId,
            assignmentStatus: row.assignment.assignmentStatus || 'ASSIGNED',
          }),
        });
        if (res.ok) {
          const updated = await res.json();
          setAssignments(prev => {
            const filtered = prev.filter(a => a.bookingTripId !== bt.bookingTripId);
            return [...filtered, updated];
          });
          ok = true;
        } else { failMsg = await readErr(res); }
      } else {
        // No trip yet — create a private trip from this booking trip.
        const res = await fetch(`${API_BASE}/api/trips/from-booking-trip/${bt.bookingTripId}?driverId=${driver.driverId}`, { method: 'POST', headers });
        if (res.ok) {
          const created = await res.json();
          setTrips(prev => [...prev, created]);
          setBookings(prev => prev.map(b => ({
            ...b,
            trips: (b.trips || []).map(x => x.bookingTripId === bt.bookingTripId ? { ...x, tripId: created.tripId } : x),
          })));
          ok = true;
        } else { failMsg = await readErr(res); }
      }
      if (ok) {
        toast({ tone: 'success', message: `${driverName} ${wasAssigned ? 'now driving' : 'assigned to'} ${row.bookingNumber}` });
      } else {
        toast({ tone: 'danger', message: failMsg || "Couldn't assign driver — try again" });
      }
    } catch (err) {
      toast({ tone: 'danger', message: "Couldn't assign driver — try again" });
    }
  };

  // Take the driver off a row entirely. Mirrors assignDriver's routing: a linked
  // trip clears its driver (and reverts ASSIGNED → SCHEDULED) via the API; a legacy
  // assignment is deleted. Optimistic state update + success/failure Toast.
  const unassignDriver = async (row) => {
    const bt = row.trip;
    const driverName = row.driver ? `${row.driver.firstName} ${row.driver.lastName}` : 'Driver';
    try {
      const headers = await getHeaders();
      let ok = false;
      let status = null;
      if (row.linkedTrip?.tripId) {
        const res = await fetch(`${API_BASE}/api/trips/${row.linkedTrip.tripId}/unassign-driver`, { method: 'PATCH', headers });
        status = res.status;
        if (res.ok) {
          const updated = await res.json();
          setTrips(prev => prev.map(t => t.tripId === updated.tripId ? updated : t));
          ok = true;
        }
      } else if (row.assignment) {
        const res = await fetch(`${API_BASE}/api/driver-assignments/${row.assignment.assignmentId}`, { method: 'DELETE', headers });
        status = res.status;
        if (res.ok) {
          setAssignments(prev => prev.filter(a => a.bookingTripId !== bt.bookingTripId));
          ok = true;
        }
      } else {
        // No trip and no assignment — nothing to unassign.
        return;
      }
      if (ok) {
        toast({ tone: 'success', message: `${driverName} unassigned from ${row.bookingNumber}` });
      } else {
        // Surface the HTTP status so a stale server / missing route (404) is obvious.
        toast({ tone: 'danger', message: `Couldn't unassign driver${status ? ` (${status})` : ''} — try again` });
      }
    } catch (err) {
      toast({ tone: 'danger', message: "Couldn't unassign driver — try again" });
    }
  };

  // The driver now performs the "confirm" step by accepting the request in the driver app
  // (ASSIGNED → CONFIRMED), which is what fires the customer email. The dispatcher's old manual
  // Confirm button was removed; an "Awaiting acceptance" indicator shows on ASSIGNED rows.

  // Remove a trip = soft-cancel its booking. Cancels by booking number, so a
  // round-trip booking's outbound + return legs (same booking number) both cancel
  // together — correct: you can't run half a round trip. Keeps the record (status →
  // CANCELLED) and notifies the customer per the backend cancel logic.
  const cancelBooking = async (row) => {
    if (cancelling) return;
    setCancelling(true);
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_BASE}/api/bookings/cancel/${encodeURIComponent(row.bookingNumber)}`, {
        method: 'PUT', headers,
      });
      if (res.ok) {
        toast({ tone: 'success', message: `${row.bookingNumber} cancelled — customer notified` });
        setCancelRow(null);
        await fetchAll();
      } else {
        toast({ tone: 'danger', message: `Couldn't cancel trip (${res.status}) — try again` });
      }
    } catch (err) {
      toast({ tone: 'danger', message: "Couldn't cancel trip — try again" });
    } finally {
      setCancelling(false);
    }
  };

  // Apply driver / vehicle / status changes from the edit modal in one shot. The standalone Trip
  // (which holds all three) is materialised ONCE up front, so changing several at once can never
  // create duplicate Trips. Only the keys present in `changes` are touched; a null value clears.
  // Returns { ok, error } — the caller refetches authoritative data (fetchAll) after.
  const applyDispatch = async (row, changes, opts = {}) => {
    const headers = await getHeaders();
    if (opts.suppress) headers['X-Suppress-Notifications'] = 'true';
    const wantsDriver = 'driverId' in changes;
    const wantsVehicle = 'vehicleId' in changes;
    const wantsStatus = 'status' in changes;
    const needsTrip = (wantsDriver && changes.driverId) || (wantsVehicle && changes.vehicleId) || wantsStatus;
    let tripId = row.linkedTrip?.tripId || null;
    if (!tripId && needsTrip) {
      try { tripId = await ensureTrip(row); }
      catch (e) { return { ok: false, error: "Couldn't create the trip record" }; }
    }
    try {
      if (wantsDriver) {
        if (changes.driverId) {
          const res = await fetch(`${API_BASE}/api/trips/${tripId}/assign-driver/${changes.driverId}`, { method: 'PATCH', headers });
          if (!res.ok) return { ok: false, error: (await readErr(res)) || `Couldn't assign driver (${res.status})` };
        } else if (tripId) {
          await fetch(`${API_BASE}/api/trips/${tripId}/unassign-driver`, { method: 'PATCH', headers });
        }
      }
      if (wantsVehicle) {
        if (changes.vehicleId) {
          const res = await fetch(`${API_BASE}/api/trips/${tripId}/assign-vehicle/${changes.vehicleId}`, { method: 'PATCH', headers });
          if (!res.ok) return { ok: false, error: `Couldn't assign vehicle (${res.status})` };
        } else if (tripId) {
          await fetch(`${API_BASE}/api/trips/${tripId}/unassign-vehicle`, { method: 'PATCH', headers });
        }
      }
      if (wantsStatus && tripId) {
        const res = await fetch(`${API_BASE}/api/trips/${tripId}/status?status=${encodeURIComponent(changes.status)}`, { method: 'PATCH', headers });
        if (!res.ok) {
          return { ok: false, error: res.status === 409
            ? 'That driver already has a trip underway — finish it first.'
            : `Couldn't set status (${res.status})` };
        }
      }
      return { ok: true };
    } catch (err) {
      return { ok: false, error: 'Some dispatch changes failed — refresh and retry' };
    }
  };

  // Dev/demo only: permanently delete a test booking + its trips through the flag-gated backend
  // service (no notifications fire). Authoritative refresh afterwards; the row disappears everywhere.
  const hardDeleteTestTrip = async (row) => {
    if (deleting) return;
    setDeleting(true);
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_BASE}/api/ops/test-tools/bookings/${row.bookingId}`, {
        method: 'DELETE', headers,
      });
      if (res.ok) {
        toast({ tone: 'success', message: `${row.bookingNumber} permanently deleted` });
        setDeleteRow(null);
        setDeleteConfirm('');
        await fetchAll();
      } else {
        toast({ tone: 'danger', message: `Couldn't delete trip (${res.status}) — try again` });
      }
    } catch (err) {
      toast({ tone: 'danger', message: "Couldn't delete trip — try again" });
    } finally {
      setDeleting(false);
    }
  };

  // Per-row overflow menu: the clearly-labelled destructive actions. "Cancel booking" is the
  // normal operational path (keeps the record); "Delete test trip" is the dev-only permanent
  // removal and only appears when the backend test tools are enabled.
  const rowMoreItems = (r) => {
    const items = [{ label: 'Cancel booking', icon: <IconX size={15} />, danger: true, onClick: () => setCancelRow(r) }];
    if (testToolsEnabled) {
      items.push({ label: 'Delete test trip', icon: <IconTrash size={15} />, danger: true, onClick: () => { setDeleteRow(r); setDeleteConfirm(''); } });
    }
    return items;
  };

  // --- Retained shared/charter linking handlers (deferred feature, hidden in 8ef23e5) ---
  // Not wired into the current dispatcher UI; kept so the feature can be re-enabled.
  const handleLinkBooking = async (tripId, bookingTripId) => {
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_BASE}/api/trips/${tripId}/link-booking-trip/${bookingTripId}`, { method: 'POST', headers });
      if (res.ok) {
        const updated = await res.json();
        setTrips(prev => prev.map(t => t.tripId === updated.tripId ? updated : t));
        setBookings(prev => prev.map(b => ({
          ...b,
          trips: (b.trips || []).map(bt => bt.bookingTripId === bookingTripId ? { ...bt, tripId } : bt),
        })));
        setLinkingTrip(null);
      }
    } catch (err) { /* no-op: handler retained for deferred feature */ }
  };

  const handleUnlinkBooking = async (tripId, bookingTripId) => {
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_BASE}/api/trips/${tripId}/unlink-booking-trip/${bookingTripId}`, { method: 'DELETE', headers });
      if (res.ok) {
        const updated = await res.json();
        setTrips(prev => prev.map(t => t.tripId === updated.tripId ? updated : t));
        setBookings(prev => prev.map(b => ({
          ...b,
          trips: (b.trips || []).map(bt => bt.bookingTripId === bookingTripId ? { ...bt, tripId: null } : bt),
        })));
      }
    } catch (err) { /* no-op: handler retained for deferred feature */ }
  };
  // eslint-disable-next-line no-unused-expressions
  [handleLinkBooking, handleUnlinkBooking, linkingTrip];

  // --- Derive rows (flatten bookings → per-booking-trip rows). Unchanged shape. ---
  const driverMap = {}, locationMap = {}, assignmentByTrip = {}, tripMap = {};
  drivers.forEach(d => { driverMap[d.driverId] = d; });
  locations.forEach(l => { locationMap[l.driverId] = l; });
  assignments.forEach(a => { assignmentByTrip[a.bookingTripId] = a; });
  trips.forEach(t => { tripMap[t.tripId] = t; });
  const rideTypeMap = {};
  rideTypes.forEach(rt => { rideTypeMap[rt.rideTypeId] = rt.rideName; });
  const customerMap = {};
  customers.forEach(c => { customerMap[c.customerId] = `${c.firstName || ''} ${c.lastName || ''}`.trim(); });

  const allRows = [];
  bookings.forEach(b => {
    (b.trips || []).forEach(bt => {
      const linkedTrip = bt.tripId ? tripMap[bt.tripId] : null;
      const assignment = assignmentByTrip[bt.bookingTripId];
      const driverId = linkedTrip?.driverId || (assignment ? assignment.driverId : null);
      const driver = driverId ? driverMap[driverId] : null;
      const loc = driver ? locationMap[driver.driverId] : null;
      allRows.push({ bookingId: b.bookingId, bookingNumber: b.bookingNumber, customerId: b.customerId, trip: bt, linkedTrip, assignment, driverId, driver, loc });
    });
  });

  // Effective status: prefer the linked Trip's status, fall back to the
  // booking-trip's own status when no Trip exists yet.
  const effStatus = (r) => ((r.linkedTrip?.tripStatus) || r.trip.tripStatus || '').toUpperCase();
  // "Needs a driver" keys on whether a driverId exists (on the linked trip or a
  // legacy assignment), NOT on whether we resolved that driver's name. A row whose
  // driverId points to a driver missing from the loaded list IS assigned — it's a
  // name-resolution gap (see the assignedUnresolved branch in renderRow), not work.
  // Status range is ACTIONABLE_PRE (incl. ASSIGNED): a driverless SCHEDULED/CONFIRMED
  // is normal new work, while a driverless ASSIGNED is anomalous but still in the
  // dispatcher's actionable range, so we let them recover it by assigning a driver.
  const needsDriver = (r) => !r.driverId && ACTIONABLE_PRE.includes(effStatus(r));
  // Row ordering: trips that still need a driver / sit in queue float to the TOP; finished
  // (completed/cancelled) trips sink to the BOTTOM; everything else in the middle. Stable otherwise.
  const rowRank = (r) => {
    if (needsDriver(r)) return 0;
    const s = effStatus(r);
    if (s === 'COMPLETED' || s === 'CANCELLED') return 2;
    return 1;
  };

  // Cancelled trips are noise on the working board — "All statuses" hides them; they're only
  // shown when the dispatcher explicitly picks the Cancelled status.
  const isCancelled = (r) => effStatus(r) === 'CANCELLED';
  const matchesStatus = (r) => {
    if (statusFilter === 'all') return !isCancelled(r);
    if (statusFilter === 'unassigned') return needsDriver(r);
    return effStatus(r) === statusFilter.toUpperCase();
  };
  const matchesWhen = (r) => {
    const dt = r.trip.pickup?.datetime;
    if (timeFilter === 'today') return isToday(dt);
    if (timeFilter === 'tomorrow') return isTomorrow(dt);
    if (timeFilter === 'week') return isThisWeek(dt);
    return true;
  };
  // Free-text search over what's loaded: customer name, booking #, full pickup/dropoff
  // address (street + city + state + airport), or the pickup date.
  const matchesSearch = (r) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const haystack = [
      r.bookingNumber,
      customerMap[r.customerId],
      formatLocation(r.trip.pickup),
      formatLocation(r.trip.dropoff),
      dateSearchText(r.trip.pickup?.datetime),
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(q);
  };

  const filteredRows = allRows.filter(r => matchesWhen(r) && matchesStatus(r) && matchesSearch(r));
  // Unassigned (the dispatcher's work) sorted to the top; order otherwise stable.
  const sortedRows = [...filteredRows].sort((a, b) => rowRank(a) - rowRank(b));

  const isGrouped = timeFilter === 'week' || timeFilter === 'all';
  const isTerminalRow = (r) => { const s = effStatus(r); return s === 'COMPLETED' || s === 'CANCELLED'; };
  const pickupMs = (r) => new Date(r.trip.pickup?.datetime || 0).getTime();

  // Priority order for the list (esp. "All statuses"): actionable trips at the TOP, completed at the
  // BOTTOM. Active rows: grouped by pickup day (Week/All) with need-a-driver bubbling up; Today/Tomorrow
  // just need-a-driver first then by time. Completed/cancelled go last (most recent first).
  const activeOrdered = filteredRows.filter(r => !isTerminalRow(r)).sort((a, b) => {
    if (isGrouped) {
      const d = daySortVal(a.trip.pickup?.datetime) - daySortVal(b.trip.pickup?.datetime);
      if (d !== 0) return d;
    }
    const n = (needsDriver(a) ? 0 : 1) - (needsDriver(b) ? 0 : 1);
    if (n !== 0) return n;
    return pickupMs(a) - pickupMs(b);
  });
  const doneOrdered = filteredRows.filter(isTerminalRow).sort((a, b) => pickupMs(b) - pickupMs(a));
  const orderedRows = [...activeOrdered, ...doneOrdered];

  // Section label for a row: day label for active rows in grouped views, "Completed"/"Cancelled"
  // for terminal rows, null for active rows in Today/Tomorrow (no header needed).
  const sectionOf = (r) => {
    if (isCancelled(r)) return 'Cancelled';
    if (isTerminalRow(r)) return 'Completed';
    return isGrouped ? dayLabel(r.trip.pickup?.datetime) : null;
  };

  // Pagination — keep the list a fixed-height paged panel instead of an endless scroll.
  const PAGE_SIZE = 12;
  const pageCount = Math.max(1, Math.ceil(orderedRows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = orderedRows.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
  // Bucket the current page's rows into contiguous sections (day / Completed) for inline headers.
  const pageSections = (() => {
    const out = [];
    pageRows.forEach(r => {
      const label = sectionOf(r);
      const last = out[out.length - 1];
      if (last && last.label === label) last.rows.push(r);
      else out.push({ label, rows: [r] });
    });
    return out;
  })();

  const selected = filteredRows.find(r => rowKey(r) === selectedId) || filteredRows[0];

  // --- KPIs (computed explicitly; not the old buggy counts) ---
  // Cancelled trips don't count toward the board's totals unless the Cancelled filter is on.
  const countableRows = statusFilter === 'cancelled' ? allRows : allRows.filter(r => !isCancelled(r));
  const todayRows = allRows.filter(r => !isCancelled(r) && isToday(r.trip.pickup?.datetime));
  const todayUnassigned = todayRows.filter(needsDriver).length;
  const activeCount = allRows.filter(r => LIVE_STATUSES.includes(effStatus(r))).length;
  const TEN_MIN = 10 * 60 * 1000;
  const onlineCount = new Set(
    locations
      .filter(l => l.recordedAt && (Date.now() - new Date(l.recordedAt).getTime()) <= TEN_MIN)
      .map(l => l.driverId)
  ).size;
  const showingCount = filteredRows.length;
  const needDriverCount = sortedRows.filter(needsDriver).length;

  const hasFilters = timeFilter !== 'all' || statusFilter !== 'all' || !!search.trim();
  const clearFilters = () => { setTimeFilter('all'); setStatusFilter('all'); setSearch(''); };
  const refreshing = loading && hasLoaded;
  // Header "Live" indicator: green only when the last fetch/poll succeeded AND the
  // data isn't stale (the 12s poll should keep lastSync fresh; >30s ≈ 2+ missed
  // polls). Otherwise show "Reconnecting…" so the dot stops claiming Live on a
  // silent poll failure.
  const syncStale = !lastSync || (Date.now() - new Date(lastSync).getTime()) > 30000;
  const connected = liveOk && !syncStale;

  const activeDrivers = drivers.filter(d => d.isActive);

  // --- Online status (live driver-location pings within the last 10 min) ---
  const onlineDriverIds = new Set(
    locations
      .filter(l => l.recordedAt && (Date.now() - new Date(l.recordedAt).getTime()) <= TEN_MIN)
      .map(l => l.driverId)
  );

  // --- Schedule-aware availability — the single source of truth for the Drivers panel ---
  // The backend returns, per active driver, their occupied windows
  // [pickup - approach - margin, pickup + trip_time + buffer] and whether they're free at the
  // current instant. "Available now" = free this instant (a later-today trip still counts as
  // free now, shown inline as "next …"); "Busy now" = inside an occupied window right now.
  const occByDriver = {};
  (occupancy || []).forEach(o => { if (o && o.driverId != null) occByDriver[o.driverId] = o; });
  const isAlgoFreeNow = (d) => { const o = occByDriver[d.driverId]; return o ? o.freeNow : true; };
  // Soonest upcoming occupied-window start (Infinity if none) — used to list the truly-free
  // ahead of the free-but-booked-later within "Available now".
  const upcomingWindowStart = (d) => {
    const nowMs = Date.now();
    const starts = (occByDriver[d.driverId]?.windows || [])
      .map(w => new Date(w.start).getTime())
      .filter(t => t > nowMs);
    return starts.length ? Math.min(...starts) : Infinity;
  };
  const algoFreeDrivers = activeDrivers.filter(isAlgoFreeNow).sort((a, b) => {
    const ra = upcomingWindowStart(a) === Infinity ? -1 : 1;   // truly-free group first
    const rb = upcomingWindowStart(b) === Infinity ? -1 : 1;
    if (ra !== rb) return ra - rb;
    return upcomingWindowStart(a) - upcomingWindowStart(b);     // then soonest next trip first
  });
  const algoBusyDrivers = activeDrivers.filter(d => !isAlgoFreeNow(d));

  // Driver dropdown for a row, capability-aware. A driver is "certified" for the
  // row when their rideTypeIds include the trip's rideTypeId. Certified drivers
  // sort first and uncertified ones are labelled "· not certified" — but ALL stay
  // selectable so the dispatcher keeps an override. If the trip has no rideTypeId,
  // or no active driver is certified for it (rideTypeIds likely unpopulated), we
  // skip the marking entirely rather than flag everyone.
  //
  // A driver is "busy" for a given row only when that row's pickup time falls
  // inside one of their occupied windows — the SAME schedule-aware occupancy the
  // Drivers panel renders. This deliberately replaces the old "has any trip in the
  // active When window" check, which blocked a driver from EVERY row (e.g. a 5am
  // trip) just because they held an unrelated later-today trip. Degrades safely: if
  // occupancy is unavailable the driver is treated as free (dispatcher keeps the
  // override), and the row's own current driver always stays selectable.
  const rowConflicts = (row, d) => {
    const pickup = row.trip.pickup?.datetime;
    if (!pickup) return [];
    const pMs = new Date(pickup).getTime();
    return (occByDriver[d.driverId]?.windows || []).filter(w =>
      pMs >= new Date(w.start).getTime() && pMs <= new Date(w.end).getTime()
    );
  };
  const driverItems = (row) => {
    const rt = row.trip.rideTypeId;
    const certified = (d) => !rt || (Array.isArray(d.rideTypeIds) && d.rideTypeIds.includes(rt));
    const enforce = rt && activeDrivers.some(certified);
    const ordered = enforce
      ? [...activeDrivers].sort((a, b) => (certified(b) ? 0 : 1) - (certified(a) ? 0 : 1))
      : activeDrivers;
    const items = ordered.map(d => {
      const current = row.driver && row.driver.driverId === d.driverId;
      const uncertified = enforce && !certified(d);
      // A busy driver can't be assigned — but the row's own current driver is
      // "busy" with this very trip, so keep them selectable (the · current entry).
      // A driver who has STARTED a trip (onTripNow) is unavailable for any other row.
      const onTrip = !current && !!occByDriver[d.driverId]?.onTripNow;
      const conflicts = current ? [] : rowConflicts(row, d);
      const busy = onTrip || conflicts.length > 0;
      const busyReason = onTrip
        ? ' · on a trip'
        : conflicts.length ? ` · busy · conflicts ${formatTime(conflicts[0].pickupAt)}` : '';
      return {
        label: `${d.firstName} ${d.lastName}${current ? ' · current' : ''}${uncertified ? ' · not certified' : ''}${busyReason}`,
        icon: current ? <IconCircleCheck size={15} /> : <IconUser size={15} />,
        disabled: busy,
        onClick: busy ? undefined : () => assignDriver(row, d),
      };
    });
    // Let the dispatcher take a driver off the trip entirely (Change rows only).
    if (row.driver) {
      items.push({
        label: 'Unassign driver',
        icon: <IconX size={15} />,
        danger: true,
        onClick: () => unassignDriver(row),
      });
    }
    return items;
  };

  // ---- Initial states (don't blank the page on a background refresh) ----
  if (loading && !hasLoaded) {
    return (
      <div className="h-full grid place-items-center bg-surface text-body/55 text-13">
        Loading dispatch…
      </div>
    );
  }
  if (error && !hasLoaded) {
    return (
      <div className="h-full grid place-items-center bg-surface p-6">
        <Alert tone="danger" title="Couldn't load dispatch" className="max-w-md">
          {error}
          <div className="mt-2"><Button variant="outline" size="sm" onClick={fetchAll}>Retry</Button></div>
        </Alert>
      </div>
    );
  }

  const renderRow = (r) => {
    const status = effStatus(r);
    // Customer name resolved from the booking's customerId via /api/customers —
    // present on every row. Falls back to the booking number if a name is missing.
    const customerName = customerMap[r.customerId] || r.bookingNumber;
    const vehLabel = vehicleLabel(r.trip, rideTypeMap[r.trip.rideTypeId]);
    const driverName = r.driver ? `${r.driver.firstName} ${r.driver.lastName}` : null;
    // Driver is assigned (driverId set) but absent from the loaded drivers list —
    // name unresolved, not unassigned. The poll backfills the name (see pollLive).
    const assignedUnresolved = !r.driver && !!r.driverId;
    const unassigned = needsDriver(r);
    const canChange = r.driver && ACTIONABLE_PRE.includes(status);
    const live = LIVE_STATUSES.includes(status);
    // Driverless but status implies a driver (ASSIGNED or in-motion) — flag it.
    // ASSIGNED is still actionable (Assign button shows); live rows are read-only.
    const noDriverWarn = !r.driverId && (status === 'ASSIGNED' || live);
    const isSel = selected && r.bookingId === selected.bookingId && r.trip.bookingTripId === selected.trip.bookingTripId;

    return (
      <div
        key={rowKey(r)}
        onClick={() => setSelectedId(rowKey(r))}
        className={cx(
          'group relative cursor-pointer px-4 py-3 transition-colors duration-150',
          isSel ? 'bg-primary/[0.045]' : 'hover:bg-primary/5'
        )}
      >
        <span className={cx(
          'absolute left-0 top-0 bottom-0 w-[3px] transition-colors',
          unassigned ? 'bg-accent' : isSel ? 'bg-primary' : 'bg-transparent'
        )} />

        {/* 1 — status · customer */}
        <div className="flex items-center gap-2">
          <StatusBadge status={status} glyph />
          <span className="text-13 font-semibold text-primary truncate">{customerName}</span>
        </div>

        {/* 2 — route */}
        <div className="mt-2 flex gap-2.5">
          <div className="flex flex-col items-center pt-1">
            <span className="w-2 h-2 rounded-full border-2 border-success bg-white shrink-0" />
            <span className="w-px flex-1 my-1 bg-border" />
            <span className="w-2 h-2 rounded-full border-2 border-danger bg-white shrink-0" />
          </div>
          <div className="min-w-0 flex-1 space-y-2 text-13 leading-snug">
            <div className="truncate text-body">{formatLocation(r.trip.pickup)}</div>
            <div className="truncate text-body">{formatLocation(r.trip.dropoff)}</div>
          </div>
        </div>

        {/* 3 — pickup time + assignment + action */}
        <div className="mt-2.5 flex items-center gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="inline-flex items-center gap-1 text-body/70">
              <IconClock size={13} className="text-body/45" />
              <span className="font-mono tabular-nums">{formatTime(r.trip.pickup?.datetime)}</span>
            </span>
          </div>

          <div className="ml-auto flex items-center gap-2.5 shrink-0">
            {vehLabel && (
              <span className="shrink-0 inline-block max-w-[140px] truncate rounded-md bg-border/50 px-1.5 py-0.5 text-11 font-medium text-body/65 align-middle">
                {vehLabel}
              </span>
            )}
            {driverName ? (
              <span className="inline-flex items-center gap-1.5 text-[12px] text-body/75 max-w-[140px]">
                <IconUser size={13} className="text-body/45 shrink-0" />
                <span className="truncate">{driverName}</span>
              </span>
            ) : status === 'CANCELLED' ? (
              <span className="text-[12px] text-body/45">No driver</span>
            ) : assignedUnresolved ? (
              <span className="inline-flex items-center gap-1.5 text-[12px] text-body/55 max-w-[140px]">
                <IconUser size={13} className="text-body/45 shrink-0" />
                <span className="truncate">Driver #{r.driverId}</span>
              </span>
            ) : noDriverWarn ? (
              <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-danger max-w-[160px]" title="Trip has no driver but its status implies one — check the booking">
                <IconWarning size={13} className="shrink-0" />
                <span className="truncate">No driver</span>
              </span>
            ) : (
              <span className="text-[12px] font-medium text-body/55">Unassigned</span>
            )}

            {/* Edit / remove trip — available pre-trip; a live/cancelled trip is locked (backend 409s too). */}
            {!live && status !== 'CANCELLED' && (
              <button
                onClick={(e) => { e.stopPropagation(); setEditRow(r); }}
                className={cx(TRIGGER_BASE, TRIGGER_OUTLINE, 'px-2')}
                title="Edit trip details"
                aria-label="Edit trip"
              >
                <IconEdit size={14} />
              </button>
            )}
            {!live && status !== 'CANCELLED' && (
              <div onClick={(e) => e.stopPropagation()}>
                <DropdownMenu
                  align="right"
                  items={rowMoreItems(r)}
                  trigger={
                    <span className={cx(TRIGGER_BASE, TRIGGER_OUTLINE, 'px-2')} title="More actions" aria-label="More actions">
                      <IconDots size={14} />
                    </span>
                  }
                />
              </div>
            )}
            {unassigned && (
              <div onClick={(e) => e.stopPropagation()}>
                <DropdownMenu
                  align="right"
                  items={driverItems(r)}
                  trigger={<span className={cx(TRIGGER_BASE, TRIGGER_PRIMARY)}>Assign</span>}
                />
              </div>
            )}
            {!unassigned && r.driver && status === 'ASSIGNED' && (
              <span
                className="inline-flex items-center gap-1.5 text-11 font-medium text-body/55"
                title="Sent to the driver — waiting for them to accept. The customer is emailed once the driver accepts."
              >
                <span className="relative flex w-1.5 h-1.5">
                  <span className="absolute inline-flex w-full h-full rounded-full bg-accent opacity-60 animate-ping" />
                  <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-accent" />
                </span>
                Awaiting acceptance
              </span>
            )}
            {!unassigned && canChange && (
              <div onClick={(e) => e.stopPropagation()}>
                <DropdownMenu
                  align="right"
                  items={driverItems(r)}
                  trigger={<span className={cx(TRIGGER_BASE, TRIGGER_OUTLINE)}>Change</span>}
                />
              </div>
            )}
            {live && (
              <span className="inline-flex items-center gap-1.5 text-11 font-medium text-body/55">
                <span className="relative flex w-1.5 h-1.5">
                  <span className="absolute inline-flex w-full h-full rounded-full bg-accent opacity-60 animate-ping" />
                  <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-accent" />
                </span>
                Live
                <ElapsedTime startedAt={r.linkedTrip?.startedAt} />
              </span>
            )}
          </div>
        </div>

      </div>
    );
  };

  // Drivers pane — Available vs Unavailable for the active When window. Pure read
  // of the derived lists above; mirrors the booking-trip list's section styling.
  // Schedule-aware row: shows the current occupied window when busy, else the next pickup.
  // "~approx" flags windows whose approach time fell back to the default (no/located driver).
  const renderAlgoDriverRow = (d) => {
    const occ = occByDriver[d.driverId];
    const windows = (occ?.windows || []).slice().sort((a, b) => new Date(a.start) - new Date(b.start));
    const online = onlineDriverIds.has(d.driverId);
    const nowMs = Date.now();
    const active = windows.find(w => nowMs >= new Date(w.start).getTime() && nowMs <= new Date(w.end).getTime());
    const next = !active ? windows.find(w => new Date(w.start).getTime() > nowMs) : null;
    const approx = !!occ && (!occ.locationKnown || windows.some(w => w.approachEstimated));
    return (
      <div key={d.driverId} className="flex items-center gap-2.5 px-4 py-2.5">
        <span className={cx('w-1.5 h-1.5 rounded-full shrink-0', online ? 'bg-success' : 'bg-border')} title={online ? 'Online' : 'Offline'} />
        <IconUser size={14} className="text-body/45 shrink-0" />
        <span className="text-13 font-medium text-primary truncate">{d.firstName} {d.lastName}</span>
        <span className="ml-auto flex items-center gap-1.5 text-[12px] whitespace-nowrap">
          {approx && <span className="text-body/35" title="Approach time estimated (driver location unknown or stale)">~approx</span>}
          {active ? (
            <span className="inline-flex items-center gap-1 font-mono tabular-nums text-body/60" title="Occupied window">
              <IconClock size={12} className="text-body/40" />{formatTime(active.start)}–{formatTime(active.end)}
            </span>
          ) : occ?.onTripNow ? (
            <span className="inline-flex items-center gap-1.5 font-medium text-accent" title="Driver has started a trip">
              <span className="relative flex w-1.5 h-1.5">
                <span className="absolute inline-flex w-full h-full rounded-full bg-accent opacity-60 animate-ping" />
                <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-accent" />
              </span>
              On trip
            </span>
          ) : next ? (
            <span className="inline-flex items-center gap-1 text-body/55">next <span className="font-mono tabular-nums">{isToday(next.pickupAt) ? '' : `${new Date(next.pickupAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, `}{formatTime(next.pickupAt)}</span></span>
          ) : (
            <span className="font-medium text-success">Free</span>
          )}
        </span>
      </div>
    );
  };

  const renderDriversPanel = () => (
    <div className="absolute inset-0 flex flex-col min-h-0 bg-white">
      <div className="shrink-0 flex items-center gap-2 px-4 py-2.5 border-b border-border">
        <h3 className="text-13 font-semibold text-primary">Drivers</h3>
        <span className="ml-auto inline-flex items-center gap-2 text-[12px]">
          <span className="inline-flex items-center gap-1 text-success"><span className="w-1.5 h-1.5 rounded-full bg-success" /><span className="font-mono tabular-nums">{algoFreeDrivers.length}</span> available</span>
          <span className="inline-flex items-center gap-1 text-body/55"><span className="w-1.5 h-1.5 rounded-full bg-accent" /><span className="font-mono tabular-nums">{algoBusyDrivers.length}</span> busy</span>
        </span>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {activeDrivers.length === 0 ? (
          <div className="p-4"><Alert tone="info" title="No active drivers">Onboard or activate drivers to see availability here.</Alert></div>
        ) : (
          <React.Fragment>
            {/* Schedule-aware availability (occupied-window algo): free RIGHT NOW vs currently busy.
                A driver appears in exactly one bucket; a later-today trip shows inline as "next …". */}
            <div className="sticky top-0 z-10 flex items-center gap-2 px-4 py-1.5 border-b border-border bg-surface/95 backdrop-blur-sm">
              <span className="text-11 font-semibold uppercase tracking-wider text-success">Available now</span>
              <span className="font-mono text-11 tabular-nums text-body/40">{algoFreeDrivers.length}</span>
              <span className="text-[10px] text-body/35 normal-case font-normal">schedule-aware</span>
            </div>
            {algoFreeDrivers.length === 0 ? (
              <div className="px-4 py-3 text-[12px] text-body/45">No active driver is free at the moment.</div>
            ) : (
              <div className="divide-y divide-border">{algoFreeDrivers.map(d => renderAlgoDriverRow(d))}</div>
            )}
            <div className="sticky top-0 z-10 flex items-center gap-2 px-4 py-1.5 border-b border-t border-border bg-surface/95 backdrop-blur-sm">
              <span className="text-11 font-semibold uppercase tracking-wider text-body/55">Busy now</span>
              <span className="font-mono text-11 tabular-nums text-body/40">{algoBusyDrivers.length}</span>
              <span className="text-[10px] text-body/35 normal-case font-normal">in an occupied window</span>
            </div>
            {algoBusyDrivers.length === 0 ? (
              <div className="px-4 py-3 text-[12px] text-body/45">No active driver is occupied right now.</div>
            ) : (
              <div className="divide-y divide-border">{algoBusyDrivers.map(d => renderAlgoDriverRow(d))}</div>
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  );

  return (
    <div className="h-full min-h-0 flex flex-col gap-4 p-5 bg-surface">
      {/* header */}
      <header className="shrink-0 flex items-center gap-3">
        <div className="min-w-0">
          <h1 className="text-15 font-bold text-primary leading-tight">Dispatch</h1>
          <div className="text-[12px] text-body/55 leading-tight">Assign drivers to unassigned trips</div>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <span className={cx('inline-flex items-center gap-1.5 text-[12px]', connected ? 'text-body/55' : 'text-primary')}>
            <span className="relative flex w-1.5 h-1.5">
              {connected && <span className="absolute inline-flex w-full h-full rounded-full bg-success opacity-60 animate-ping" />}
              <span className={cx('relative inline-flex w-1.5 h-1.5 rounded-full', connected ? 'bg-success' : 'bg-accent')} />
            </span>
            {connected ? 'Live' : 'Reconnecting…'}
          </span>
        </div>
      </header>

      {/* KPI strip */}
      <div className="shrink-0 grid grid-cols-5 gap-3">
        <Kpi label="Today" value={todayRows.length} sub={todayUnassigned ? `${todayUnassigned} unassigned` : 'all assigned'} subTone={todayUnassigned ? 'amber' : 'muted'} />
        <Kpi label="Active" value={activeCount} sub="trips in motion" />
        <Kpi label="Online" value={onlineCount} sub="drivers online" />
        <Kpi label="Available now" value={algoFreeDrivers.length} sub={`${algoBusyDrivers.length} busy now`} />
        <Kpi label="Showing" value={showingCount} sub={`of ${countableRows.length} trips`} />
      </div>

      {/* toolbar */}
      <div className="shrink-0 flex items-center gap-4 flex-wrap">
        <TextInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, booking #, address, date…"
          icon={<IconSearch size={15} />}
          className="w-[260px]"
          aria-label="Search trips"
        />
        <div className="flex items-center gap-2.5">
          <span className="text-11 font-semibold uppercase tracking-brand text-body/45">When</span>
          <Segmented options={WHEN_OPTIONS} value={timeFilter} onChange={setTimeFilter} />
        </div>
        <div className="flex items-center gap-2.5 ml-auto">
          <span className="text-11 font-semibold uppercase tracking-brand text-body/45">Status</span>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-[150px]">
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-11 font-mono text-body/45 whitespace-nowrap hidden lg:inline">Synced {relTime(lastSync)}</span>
          <Button variant="outline" size="md" onClick={refresh} aria-label="Refresh" className="px-2.5">
            <IconRefresh size={16} className={refreshing ? 'animate-spin' : ''} />
          </Button>
        </div>
        {!mapCollapsed && (
          <Segmented
            options={[{ value: 'map', label: 'Map' }, { value: 'drivers', label: 'Drivers' }]}
            value={rightTab}
            onChange={setRightTab}
          />
        )}
        <Button variant="outline" size="md" onClick={() => setMapCollapsed((c) => !c)} aria-pressed={!mapCollapsed} className="gap-1.5">
          <IconPin size={15} />
          <span className="hidden sm:inline">{mapCollapsed ? 'Show panel' : 'Hide panel'}</span>
        </Button>
      </div>

      {/* selected-trip bar — always visible up top so the booking number is seen on selection,
          even when the map panel (which used to host this chip) is hidden. */}
      {selected && (
        <div className="shrink-0 flex items-center gap-3 rounded-card border border-border bg-white px-3.5 py-2.5">
          <StatusBadge status={effStatus(selected)} glyph />
          <span className="font-mono text-13 font-semibold text-primary">{selected.bookingNumber}</span>
          <span className="text-[12px] text-body/55 truncate hidden sm:inline">{shortLoc(selected.trip.pickup)} → {shortLoc(selected.trip.dropoff)}</span>
          <span className="font-mono text-[12px] text-body/55 tabular-nums whitespace-nowrap">{dayLabel(selected.trip.pickup?.datetime)} · {formatTime(selected.trip.pickup?.datetime)}</span>
          <span className="ml-auto font-mono text-13 font-semibold text-primary tabular-nums">{fmtUSD(selected.trip.estimatedAmount)}</span>
        </div>
      )}

      {/* list-first layout; map is a secondary, collapsible pane */}
      <div className={cx('flex-1 min-h-0 grid gap-4', mapCollapsed ? 'grid-cols-1' : 'grid-cols-[3fr_2fr]')}>
        {/* list */}
        <Card className="flex flex-col min-h-0 overflow-hidden">
          <div className="shrink-0 flex items-center gap-2 px-4 py-2.5 border-b border-border bg-white">
            <h3 className="text-13 font-semibold text-primary">Booking trips</h3>
            <span className="rounded-md px-1.5 py-0.5 text-11 font-mono font-semibold tabular-nums bg-border/60 text-body">{sortedRows.length}</span>
            <span className="ml-auto inline-flex items-center gap-1.5 text-[12px]">
              {needDriverCount > 0 ? (
                <React.Fragment>
                  <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                  <span className="font-medium text-primary"><span className="font-mono">{needDriverCount}</span> need a driver</span>
                </React.Fragment>
              ) : (
                <span className="text-body/50">All assigned</span>
              )}
            </span>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
            {orderedRows.length === 0 ? (
              <div className="p-4">
                <Alert tone="info" title="No trips">
                  {hasFilters
                    ? 'Nothing matches the current filters.'
                    : 'Nothing scheduled for this window. New bookings appear here automatically.'}
                  {hasFilters && (
                    <div className="mt-2"><Button variant="ghost" size="sm" onClick={clearFilters}>Clear filters</Button></div>
                  )}
                </Alert>
              </div>
            ) : (
              pageSections.map((s, i) => (
                <div key={s.label ? `${s.label}-${i}` : `sec-${i}`}>
                  {s.label && (
                    <div className="sticky top-0 z-10 flex items-center gap-2 px-4 py-1.5 border-b border-border bg-surface/95 backdrop-blur-sm">
                      <span className="text-11 font-semibold uppercase tracking-wider text-body/55">{s.label}</span>
                    </div>
                  )}
                  <div className="divide-y divide-border">{s.rows.map(renderRow)}</div>
                </div>
              ))
            )}
          </div>
          {pageCount > 1 && (
            <div className="shrink-0 border-t border-border px-3 py-2 flex justify-center">
              <Pagination page={safePage + 1} pageCount={pageCount} onChange={(p) => setPage(p - 1)} />
            </div>
          )}
        </Card>

        {/* secondary, collapsible pane — Map or Drivers (availability) */}
        {!mapCollapsed && (
        <div className="relative min-h-0 overflow-hidden rounded-card border border-border bg-white shadow-card">
          {rightTab === 'drivers' ? renderDriversPanel() : (
          <React.Fragment>
          {isLoaded ? (
            <GoogleMap key={`map-${timeFilter}-${statusFilter}`} mapContainerStyle={mapStyle} center={DEFAULT_CENTER} zoom={10}
              onLoad={(map) => {
                mapRef.current = map;
                const bounds = new window.google.maps.LatLngBounds();
                let h = false;
                filteredRows.forEach(r => {
                  if (r.trip.pickup?.latitude) { bounds.extend({ lat: parseFloat(r.trip.pickup.latitude), lng: parseFloat(r.trip.pickup.longitude) }); h = true; }
                  if (r.trip.dropoff?.latitude) { bounds.extend({ lat: parseFloat(r.trip.dropoff.latitude), lng: parseFloat(r.trip.dropoff.longitude) }); h = true; }
                  if (r.loc) { bounds.extend({ lat: parseFloat(r.loc.latitude), lng: parseFloat(r.loc.longitude) }); h = true; }
                });
                if (h) map.fitBounds(bounds, 50);
              }}>
              {filteredRows.map((r, idx) => {
                const p = r.trip.pickup, d = r.trip.dropoff;
                const pp = p?.latitude ? { lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) } : null;
                const dp = d?.latitude ? { lat: parseFloat(d.latitude), lng: parseFloat(d.longitude) } : null;
                const drv = r.loc ? { lat: parseFloat(r.loc.latitude), lng: parseFloat(r.loc.longitude) } : null;
                const isSel = selected && r.bookingId === selected.bookingId && r.trip.bookingTripId === selected.trip.bookingTripId;
                const c = isSel ? '#2563EB' : ROUTE_COLORS[idx % ROUTE_COLORS.length];
                const o = isSel ? 1 : 0.35;
                const w = isSel ? 4 : 2;
                return (
                  <React.Fragment key={rowKey(r)}>
                    {pp && <Marker position={pp} icon={`http://maps.google.com/mapfiles/ms/icons/${isSel?'green':'ltblue'}-dot.png`} opacity={isSel?1:0.5} onClick={() => setSelectedId(rowKey(r))} title={`Pickup: ${shortLoc(p)}`} />}
                    {dp && <Marker position={dp} icon={`http://maps.google.com/mapfiles/ms/icons/${isSel?'red':'pink'}-dot.png`} opacity={isSel?1:0.5} onClick={() => setSelectedId(rowKey(r))} title={`Dropoff: ${shortLoc(d)}`} />}
                    {drv && <Marker position={drv} icon={`http://maps.google.com/mapfiles/ms/icons/${isSel?'blue':'purple'}-dot.png`} opacity={isSel?1:0.5} onClick={() => setSelectedId(rowKey(r))} zIndex={isSel?100:10} title={r.driver?`${r.driver.firstName} ${r.driver.lastName}`:'Driver'} />}
                    {pp && dp && !drv && <Polyline path={[pp,dp]} options={{ strokeColor:c, strokeOpacity:o, strokeWeight:w, geodesic:true, icons:[{icon:{...ARROW_ICON,fillColor:c,fillOpacity:o},offset:'25%'},{icon:{...ARROW_ICON,fillColor:c,fillOpacity:o},offset:'50%'},{icon:{...ARROW_ICON,fillColor:c,fillOpacity:o},offset:'75%'}] }} />}
                    {drv && pp && <Polyline path={[pp,drv]} options={{ strokeColor:'#16A34A', strokeOpacity:isSel?0.7:0.2, strokeWeight:w, geodesic:true }} />}
                    {drv && dp && <Polyline path={[drv,dp]} options={{ strokeColor:c, strokeOpacity:o, strokeWeight:w, geodesic:true, icons:[{icon:{...ARROW_ICON,fillColor:c,fillOpacity:o},offset:'30%'},{icon:{...ARROW_ICON,fillColor:c,fillOpacity:o},offset:'60%'},{icon:{...ARROW_ICON,fillColor:c,fillOpacity:o},offset:'90%'}] }} />}
                  </React.Fragment>
                );
              })}
            </GoogleMap>
          ) : (
            <div className="absolute inset-0 grid place-items-center" style={{ background: 'radial-gradient(120% 90% at 70% 20%, #EEF2F6 0%, #E6ECF1 55%, #DFE6EC 100%)' }}>
              <div className="flex flex-col items-center gap-2 text-body/45">
                <IconPin size={24} className="text-body/35" />
                <span className="text-13">Select a trip to preview its route</span>
                <span className="text-10 text-body/40">Google Map mounts here</span>
              </div>
            </div>
          )}

          {/* (selected-trip summary moved to a top bar above the list so it stays visible
              even when this map panel is hidden) */}
          </React.Fragment>
          )}
        </div>
        )}
      </div>

      {editRow && (
        <TripEditModal
          row={{ ...editRow, customerName: customerMap[editRow.customerId] }}
          customer={customers.find((c) => String(c.customerId) === String(editRow.customerId))}
          drivers={activeDrivers}
          vehicles={vehicles}
          getHeaders={getHeaders}
          applyDispatch={applyDispatch}
          testToolsEnabled={testToolsEnabled}
          onClose={() => setEditRow(null)}
          onSaved={() => { setEditRow(null); fetchAll(); }}
          toast={toast}
        />
      )}

      {cancelRow && (
        <Modal
          open
          onClose={() => (cancelling ? null : setCancelRow(null))}
          title="Cancel booking?"
          size="sm"
          footer={
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" size="md" onClick={() => setCancelRow(null)} disabled={cancelling}>Keep booking</Button>
              <Button variant="danger" size="md" onClick={() => cancelBooking(cancelRow)} disabled={cancelling}>
                {cancelling ? 'Cancelling…' : 'Cancel booking'}
              </Button>
            </div>
          }
        >
          <div className="space-y-3 text-13 text-body">
            <p>
              Cancel <span className="font-semibold text-primary">{customerMap[cancelRow.customerId] || cancelRow.bookingNumber}</span>
              {cancelRow.bookingNumber && <span className="font-mono text-body/60"> · {cancelRow.bookingNumber}</span>}?
            </p>
            <p className="text-body/70">
              {formatLocation(cancelRow.trip.pickup)} → {formatLocation(cancelRow.trip.dropoff)}
            </p>
            <p className="text-body/70">The booking is marked cancelled and the record is kept. The customer is notified per the normal settings. This can't be undone from here.</p>
          </div>
        </Modal>
      )}

      {deleteRow && (
        <Modal
          open
          onClose={() => (deleting ? null : setDeleteRow(null))}
          title="Delete test trip?"
          size="sm"
          tone="light"
          footer={
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" size="md" onClick={() => setDeleteRow(null)} disabled={deleting}>Keep</Button>
              <Button
                variant="danger"
                size="md"
                onClick={() => hardDeleteTestTrip(deleteRow)}
                disabled={deleting || deleteConfirm.trim().toUpperCase() !== 'DELETE'}
              >
                {deleting ? 'Deleting…' : 'Permanently delete'}
              </Button>
            </div>
          }
        >
          <div className="space-y-3 text-13 text-body">
            <div className="rounded-md bg-danger/10 px-3 py-2 text-[12px] font-medium text-danger">
              Permanent — this removes the booking, its trip legs, driver assignment, invoice and payout
              records. It cannot be undone. Drivers, vehicles and other bookings are untouched.
            </div>
            <div className="space-y-1">
              <div><span className="text-body/55">Customer</span> · <span className="font-semibold text-primary">{customerMap[deleteRow.customerId] || '—'}</span></div>
              <div><span className="text-body/55">Booking</span> · <span className="font-mono">{deleteRow.bookingNumber}</span></div>
              <div><span className="text-body/55">Route</span> · {formatLocation(deleteRow.trip.pickup)} → {formatLocation(deleteRow.trip.dropoff)}</div>
              <div><span className="text-body/55">When</span> · {formatTime(deleteRow.trip.pickup?.datetime)}</div>
              <div><span className="text-body/55">Status</span> · {effStatus(deleteRow) || '—'}</div>
              <div><span className="text-body/55">Driver</span> · {deleteRow.driver ? `${deleteRow.driver.firstName} ${deleteRow.driver.lastName}` : 'Unassigned'}</div>
              {(bookings.find(b => b.bookingId === deleteRow.bookingId)?.trips?.length || 1) > 1 && (
                <div className="text-accent">This booking has multiple legs — all will be deleted.</div>
              )}
            </div>
            <Field label={<span>Type <span className="font-mono font-semibold">DELETE</span> to confirm</span>}>
              <TextInput value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder="DELETE" autoFocus />
            </Field>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default OpsDispatchBoard;
